"use client";

import { Package, Check } from "lucide-react";
import { AnimateOnScroll, StaggeredList } from "./index";

const projects = [
  {
    name: "apollo-io-mcp",
    repo: "Rockship-Team/apollo-io-mcp",
    time: "29 days ago",
  },
  {
    name: "isense-backend",
    repo: "Rockship-Team/isense-backend",
    time: "about 1 month ago",
  },
  {
    name: "carta-frontend",
    repo: "Rockship-Team/carta-frontend",
    time: "4 days ago",
  },
  {
    name: "Documatch",
    repo: "Ne4nf/Documatch",
    time: "12 days ago",
  },
];

export function UIPreview() {
  return (
    <section
      id="preview"
      className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-b from-bg-alt to-white"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <AnimateOnScroll variant="fade-up" className="text-center max-w-[640px] mx-auto mb-8 sm:mb-12 md:mb-16">
          <span className="inline-block text-[10px] sm:text-xs font-semibold text-primary uppercase tracking-[1.5px] mb-3 sm:mb-4">
            Product Preview
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight mb-3 sm:mb-4 text-text">
            Powerful dashboard for your entire team
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-text-muted leading-relaxed">
            Manage projects, track specs, and collaborate with AI—all from one
            intuitive interface.
          </p>
        </AnimateOnScroll>

        {/* Preview Container */}
        <div className="relative max-w-[1100px] mx-auto">
          {/* Main Preview */}
          <AnimateOnScroll variant="scale" delay={100}>
            <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-[20px] shadow-xl sm:shadow-2xl overflow-hidden border border-border">
              {/* Browser Chrome */}
              <div className="bg-[#FAFBFC] px-3 sm:px-4 py-2.5 sm:py-3.5 flex items-center gap-2 sm:gap-3 border-b border-border">
                <div className="flex gap-1.5 sm:gap-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#FF5F56]" />
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#FFBD2E]" />
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#27C93F]" />
                </div>
              </div>

              {/* Projects View */}
              <div className="p-4 sm:p-6 md:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <h2 className="text-xl sm:text-2xl md:text-[28px] font-bold text-text">
                      Projects
                    </h2>
                    <span className="bg-primary/10 text-primary text-[11px] sm:text-[13px] font-semibold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full">
                      8 projects
                    </span>
                  </div>
                  <button className="flex items-center justify-center gap-1.5 sm:gap-2 bg-primary text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold w-full sm:w-fit transition-transform hover:scale-105">
                    <span>+</span> New Project
                  </button>
                </div>

                <StaggeredList
                  className="flex flex-col gap-2 sm:gap-3"
                  staggerDelay={80}
                  initialDelay={200}
                  duration={400}
                >
                  {projects.map((project) => (
                    <div
                      key={project.name}
                      className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 md:px-5 bg-white border border-border rounded-lg sm:rounded-xl transition-all hover:border-primary hover:bg-primary/5"
                    >
                      <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-primary/10 rounded-md sm:rounded-lg flex items-center justify-center text-primary">
                        <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm sm:text-[15px] font-semibold text-text truncate">
                          {project.name}
                        </div>
                        <div className="text-[10px] sm:text-xs text-text-muted font-mono truncate">
                          {project.repo}
                        </div>
                      </div>
                      <div className="text-[10px] sm:text-xs text-text-muted hidden sm:block whitespace-nowrap">
                        {project.time}
                      </div>
                    </div>
                  ))}
                </StaggeredList>
              </div>
            </div>
          </AnimateOnScroll>

          {/* Floating Preview Cards - Hidden on mobile/tablet */}
          <div className="hidden xl:block absolute top-5 -right-16 bg-white rounded-2xl p-5 shadow-2xl w-[280px] animate-float z-10">
            <div className="border border-border rounded-xl overflow-hidden">
              <div className="px-4 py-3.5 border-b border-border flex items-center gap-2.5">
                <div className="w-7 h-7 bg-primary/10 rounded-md flex items-center justify-center text-primary">
                  <Package className="w-3.5 h-3.5" />
                </div>
                <span className="text-[15px] font-semibold text-text">
                  Create Project
                </span>
              </div>
              <div className="p-4">
                <div className="mb-3">
                  <div className="text-[11px] font-semibold text-text-muted mb-1.5">
                    Repository
                  </div>
                  <div className="bg-bg-alt border border-border rounded-lg px-3 py-2.5 text-[13px] text-text">
                    Rockship-Team/carta-wcs
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-text-muted mb-1.5">
                    Project Name
                  </div>
                  <div className="bg-bg-alt border border-border rounded-lg px-3 py-2.5 text-[13px] text-text">
                    carta-wcs
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 border-t border-border flex justify-end gap-2">
                <button className="px-4 py-2 rounded-lg text-[13px] font-semibold bg-white text-text border border-border transition-colors hover:bg-bg-alt">
                  Cancel
                </button>
                <button className="px-4 py-2 rounded-lg text-[13px] font-semibold bg-primary text-white transition-transform hover:scale-105">
                  Create
                </button>
              </div>
            </div>
          </div>

          <div className="hidden xl:flex absolute bottom-16 -left-10 bg-white rounded-2xl px-5 py-4 shadow-2xl items-center gap-3 animate-float-delayed z-10">
            <div className="w-9 h-9 bg-success/10 rounded-lg flex items-center justify-center text-success">
              <Check className="w-5 h-5" />
            </div>
            <div>
              <div className="font-semibold text-text text-[13px]">
                Connected via GitHub App
              </div>
              <div className="text-text-muted text-xs">
                Webhook configured automatically
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
